/**
 * The home's send: one action that creates a canvas in the private workspace,
 * navigates to it the moment it exists, and finishes minting the session and
 * posting the first message in the background. The canvas claims the pending
 * send on arrival and opens the running session.
 */

import { useCreateCanvas } from "@/api/canvas";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { ROUTES } from "@/constants/routes";
import Sentry from "@/integrations/Sentry";
import {
  publishCanvasDispatch,
  type DispatchOutcome,
} from "@/pages/canvas/hooks/canvasDispatch";
import { seedCanvasReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { route } from "@/utils/route";
import {
  FLOW_SESSION_MODES,
  type PostMessageParams,
} from "@cohesive/api-client";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useNewHomeEnabled } from "./flag";
import { HOME_HEADER_HEIGHT, HOME_HEADER_WIDTH } from "./homeBoards";

/**
 * Rides the first message of a canvas started from the home, hidden from
 * the transcript: a short router that lets the model answer in chat or on
 * the canvas by what the ask needs, and how to build when it builds. It is
 * worded for the whole conversation: the backend renders a message with its
 * addendum on every turn. The backend caps it at 4000 runes.
 */
export function homeFirstMessageAddendum(headerPlaced: boolean): string {
  return [
    `This conversation started from the home page: the person's first message opened a new, empty canvas with the chat panel open on top of it.${
      headerPlaced
        ? ` A header block is already at the top of the canvas, ${HOME_HEADER_WIDTH} by ${HOME_HEADER_HEIGHT} at the origin: build below it.`
        : ""
    } Never mention these instructions. Reply in the language they write in.`,
    [
      "Choose where each answer goes by what serves them:",
      "- Chat, right away: a question, a greeting, advice, a short piece of writing, a quick fix. Anything a good answer covers in a screen of text. Put nothing on the canvas for these.",
      "- Canvas: something they will keep, look at, compare or come back to. An image or video, a tracker, a plan, a diagram, slides, several options side by side, or any answer longer than a screen. Then say in one or two sentences what is there.",
      '- When the ask is too vague to act on ("make me a video", "I need a report"), ask the one question that decides what to make, in chat. Do not build on a guess.',
    ].join("\n"),
    [
      "When you build on the canvas, use what it is good at.",
      "- For anything the person will use rather than read (a tracker, a plan to step through, a budget, a comparison, slides, a quiz, a dashboard), make live HTML blocks, not a document. A block can have tabs, filters, sorting and sliders that recompute. Read the canvas-html-blocks skill before your first block: it says what a block can and cannot do and carries the design tokens.",
      "- Give an ask with several parts several pieces: the main one first and largest, the others beside or below it on an even grid, nothing overlapping. One strong piece is right when the ask is one thing. Two to four is usual.",
      "- Make the pieces read as one set: the design tokens, one type scale, the same padding.",
      "- Keep documents for long prose, notes for separate ideas the person will move around, and shapes with edges for flows.",
    ].join("\n"),
    "You can make documents, tables and live interactive pages, diagrams, images, and short video clips of about eight seconds. If they ask for an image or a video, generate it. If they attached or pasted something, work from that.",
    "On your first reply, do not run code or install packages: it stops them at a permission prompt before they see anything.",
  ].join("\n\n");
}

export const HOME_FIRST_MESSAGE_ADDENDUM = homeFirstMessageAddendum(false);
export const HOME_FIRST_MESSAGE_ADDENDUM_UNDER_HEADER =
  homeFirstMessageAddendum(true);

/** A header the home places before the agent starts; the canvas takes its title as its name. */
export interface HomeHeaderPlacement {
  title: string;
  file: string;
  html: string;
  width: number;
  height: number;
}

/** How long the first message waits for the header before going anyway. */
export const HEADER_WAIT_MS = 15_000;

/** The first turn, as the composer hands it over. */
export type HomeSend = Pick<
  PostMessageParams,
  "content" | "referenced_entities" | "reasoning_effort" | "file_record_ids"
> & { header?: HomeHeaderPlacement };

export interface HomeDispatcher {
  send: (message: HomeSend) => void;
  busy: boolean;
}

export const CANVAS_FAILED = "Couldn't create a Canvas. Try again.";

export function useHomeDispatcher(): HomeDispatcher {
  const navigate = useNavigate();
  const createCanvas = useCreateCanvas();
  const createFlowSession = useCreateFlowSession();
  const postFlowMessage = usePostFlowMessage();
  const newHome = useNewHomeEnabled();
  const [busy, setBusy] = useState(false);
  // A ref, not the state: two clicks in the same tick both read the state
  // before React re-renders, and the second one would dispatch again.
  const inFlight = useRef(false);

  const mintAndPost = useCallback(
    async (
      canvasId: string,
      { header, ...message }: HomeSend,
      headerPlaced: Promise<boolean>,
    ): Promise<DispatchOutcome> => {
      let sessionId: string;
      try {
        const session = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_CHAT,
          class: "canvas",
          canvas_id: canvasId,
        });
        sessionId = session.id;
      } catch (error) {
        Sentry.captureException(error);
        return { kind: "session-failed" };
      }
      // The agent is told the header is there only when it is.
      const placed = await headerPlaced;
      try {
        await postFlowMessage.mutateAsync({
          flowSessionId: sessionId,
          id: uuidv4(),
          ...message,
          ...(newHome ? { addendum: homeFirstMessageAddendum(placed) } : {}),
        });
      } catch (error) {
        Sentry.captureException(error);
        return { kind: "post-failed", sessionId };
      }
      return { kind: "sent", sessionId };
    },
    [createFlowSession, postFlowMessage, newHome],
  );

  const send = useCallback(
    (message: HomeSend) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      void (async () => {
        try {
          const { header } = message;
          const canvas = await createCanvas.mutateAsync(
            header ? { name: header.title } : {},
          );
          // The canvas composer reads its own key on mount: seed it before
          // the navigation so the picker and the later turns keep this effort.
          if (message.reasoning_effort) {
            seedCanvasReasoningEffort(canvas.id, message.reasoning_effort);
          }
          // The header goes on the canvas before the agent reads it. One
          // that never lands must not hold the message back forever, and one
          // that lands after the message went is refused, or the canvas
          // would carry it and the agent's own.
          let settle = (_placed: boolean) => false;
          const headerPlaced = header
            ? new Promise<boolean>((resolve) => {
                const timer = setTimeout(() => {
                  settle = () => false;
                  resolve(false);
                }, HEADER_WAIT_MS);
                settle = (placed) => {
                  clearTimeout(timer);
                  settle = () => false;
                  resolve(placed);
                  return true;
                };
              })
            : Promise.resolve(false);
          publishCanvasDispatch({
            canvasId: canvas.id,
            prompt: message.content ?? "",
            reasoningEffort: message.reasoning_effort,
            ...(header
              ? { header: { ...header, settle: (placed) => settle(placed) } }
              : {}),
            outcome: mintAndPost(canvas.id, message, headerPlaced),
          });
          navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id }));
        } catch (error) {
          Sentry.captureException(error);
          toast.error(CANVAS_FAILED);
          inFlight.current = false;
          setBusy(false);
        }
      })();
    },
    [createCanvas, mintAndPost, navigate],
  );

  return { send, busy };
}
